import React from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { TranslateFunction } from 'react-localize-redux';

import { Currency } from '../../../core/api/general/general.types';
import { MenuItem } from '../../../core/api/menu/menu.types';
import { Typography } from '../../atoms/Typography';
import { MenuItemsListSkeleton } from './components/MenuItemsListSkeleton';
import { MenuListItem } from './components/MenuListItem';
import {
  BasketItems,
  OnAddItem,
  OnClickItem,
  OnRemoveItem,
  Variant,
} from './types';

export type Props = {
  basketItems: BasketItems;
  className?: string;
  currency: Currency;
  hasBorder?: boolean;
  hasGreyBackground?: boolean;
  isLoading?: boolean;
  languageCode: string;
  menuItems: MenuItem[];
  onAddItem: OnAddItem;
  onClickItem?: OnClickItem;
  onRemoveItem?: OnRemoveItem;
  title?: string;
  translate: TranslateFunction;
  variant?: Variant;
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    padding: theme.spacing(2, 0),

    '&.grey-background': {
      backgroundColor: theme.palette.grey[200],
      margin: theme.spacing(0, -2),
      padding: theme.spacing(2, 2),
    },
  },
  title: {
    paddingBottom: theme.spacing(1),
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    margin: 0,
    padding: 0,

    '&.border': {
      borderTop: `1px solid ${theme.palette.grey[400]}`,
      borderBottom: `1px solid ${theme.palette.grey[400]}`,
    },
    '&.variant': {
      '&--list': {
        '& .MuiCard-root': {
          border: `1px solid ${theme.palette.grey[400]}`,
          marginBottom: theme.spacing(2),
        },
        '& .MuiCard-root:last-child': {
          marginBottom: 0,
        },
      },
      '&--compact': {
        '& .MuiCard-root': {
          borderBottom: `1px solid ${theme.palette.grey[400]}`,
        },
        '& .MuiCard-root:last-child': {
          borderBottom: 'none',
        },
      },
      '&--grid': {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: theme.spacing(2),

        '& .MuiCard-root': {
          alignItems: 'space-between',
          border: `1px solid ${theme.palette.grey[400]}`,
          display: 'flex',
          flexBasis: `calc(50% - ${theme.spacing(1)})`,
        },
      },
    },
  },
}));

export function MenuItemsList(props: Props): JSX.Element {
  const {
    basketItems,
    className = '',
    currency,
    hasBorder = false,
    hasGreyBackground = false,
    isLoading = false,
    languageCode,
    menuItems,
    onAddItem,
    onClickItem,
    onRemoveItem,
    title = '',
    translate,
    variant = 'list',
  } = props;
  const classes = useStyles();

  const rootStyles = clsx(
    classes.root,
    'fd-menu-items-list',
    hasGreyBackground && 'grey-background',
    className,
  );
  const listStyles = clsx(
    classes.list,
    'fd-menu-items-list__menu-items',
    hasBorder && 'border',
    variant === 'list' && 'variant--list',
    variant === 'compact' && 'variant--compact',
    variant === 'grid' && 'variant--grid',
  );

  if (isLoading) {
    return (
      <div className={rootStyles}>
        <MenuItemsListSkeleton
          hasGreyBackground={hasGreyBackground}
          title={title}
          variant={variant}
        />
      </div>
    );
  }

  const renderMenuItemsList = (): JSX.Element => (
    <ul className={listStyles}>
      {menuItems.map((menuItem) => (
        <MenuListItem
          basketItems={basketItems}
          currency={currency}
          key={`list-menu-item-${menuItem.MenuItemId}`}
          languageCode={languageCode}
          menuItem={menuItem}
          onAddItem={onAddItem}
          onClickItem={onClickItem}
          onRemoveItem={onRemoveItem}
          translate={translate}
        />
      ))}
    </ul>
  );

  return (
    <div className={rootStyles}>
      {title && (
        <Typography
          className={classes.title}
          data-fd="menu-items-list-title"
          variant="h3"
        >
          {title}
        </Typography>
      )}

      {renderMenuItemsList()}
    </div>
  );
}
